/**
 * Ownership & Approval Page
 * Multi-level approval workflows and ownership management for evidence
 * 
 * Superpower #2: Ownership & Accountability
 * - Track who owns what evidence
 * - Multi-level approval chain (L1 → L2 → L3)
 * - SLA tracking with overdue alerts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Clock, 
  Users,
  TrendingUp,
  HelpCircle,
  Download,
  RefreshCw
} from 'lucide-react';

// Import approval components
import {
  ApprovalHero,
  MyApprovalsTab,
  TeamApprovalsTab,
  AllEvidenceTab,
  AnalyticsTab,
  ApprovalModal
} from '../../components/approval';
import { apiFetch } from '../../services/api_service';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OwnershipApprovalPage() {
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('my-approvals');
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);
  const [teamActivity, setTeamActivity] = useState(null);
  const [allEvidence, setAllEvidence] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'approve',
    item: null,
    isSubmitting: false
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const ITEMS_PER_PAGE = 50;
  
  // Get current user from localStorage
  const getCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };
  
  const currentUser = getCurrentUser();
  const userId = currentUser?.id || 'system';
  
  // Fetch approval stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await apiFetch(`/v1/approvals/stats`);
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [userId]);
  
  // Fetch my approval queue
  const fetchQueue = useCallback(async () => {
    try {
      const data = await apiFetch(`/v1/approvals/my-queue?status_filter=pending`);
      setQueue(data);
    } catch (err) {
      console.error('Error fetching queue:', err);
    }
  }, [userId]);
  
  // Fetch team activity
  const fetchTeamActivity = useCallback(async () => {
    try {
      const data = await apiFetch(`/v1/approvals/team`);
      setTeamActivity(data);
    } catch (err) {
      console.error('Error fetching team activity:', err);
    }
  }, [userId]);
  
  // Fetch all evidence
  const fetchAllEvidence = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * ITEMS_PER_PAGE;
      let url = `${API_URL}/api/v1/approvals/all-evidence?skip=${skip}&limit=${ITEMS_PER_PAGE}`;
      if (statusFilter && statusFilter !== 'all') {
        url += `&status_filter=${statusFilter}`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch evidence');
      const data = await res.json();
      setAllEvidence(data);
    } catch (err) {
      console.error('Error fetching evidence:', err);
    }
  }, [currentPage, statusFilter]);
  
  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await apiFetch(`/v1/approvals/analytics`);
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        await Promise.all([fetchStats(), fetchQueue()]);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
  }, [fetchStats, fetchQueue]);
  
  // Load data based on active tab
  useEffect(() => {
    if (activeTab === 'team' && !teamActivity) {
      fetchTeamActivity();
    } else if (activeTab === 'all-evidence') {
      fetchAllEvidence();
    } else if (activeTab === 'analytics' && !analytics) {
      fetchAnalytics();
    }
  }, [activeTab, teamActivity, analytics, fetchTeamActivity, fetchAllEvidence, fetchAnalytics]);
  
  // Refresh all data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchQueue(),
        activeTab === 'team' ? fetchTeamActivity() : Promise.resolve(),
        activeTab === 'all-evidence' ? fetchAllEvidence() : Promise.resolve(),
        activeTab === 'analytics' ? fetchAnalytics() : Promise.resolve()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  // Handle approve click
  const handleApproveClick = (item) => {
    setModalState({
      isOpen: true,
      mode: 'approve',
      item,
      isSubmitting: false
    });
  };
  
  // Handle reject click
  const handleRejectClick = (item) => {
    setModalState({
      isOpen: true,
      mode: 'reject',
      item,
      isSubmitting: false
    });
  };
  
  // Handle modal submit
  const handleModalSubmit = async (data) => {
    setModalState(prev => ({ ...prev, isSubmitting: true }));
    
    try {
      const endpoint = modalState.mode === 'approve'
        ? `/v1/approvals/${modalState.item.id}/approve`
        : `/v1/approvals/${modalState.item.id}/reject`;

      const res = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res?._error) {
        throw new Error(res.message || 'Operation failed');
      }
      
      // Refresh data
      await Promise.all([fetchStats(), fetchQueue()]);
      setSelectedIds([]);
      
      // Close modal
      setModalState({ isOpen: false, mode: 'approve', item: null, isSubmitting: false });
      
    } catch (err) {
      console.error('Error:', err);
      alert(err.message || 'Operation failed');
    } finally {
      setModalState(prev => ({ ...prev, isSubmitting: false }));
    }
  };
  
  // Handle bulk approve
  const handleBulkApprove = async (ids) => {
    if (!ids || ids.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to approve ${ids.length} items?`)) {
      return;
    }
    
    try {
      const result = await apiFetch(`/v1/approvals/bulk-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ evidenceIds: ids, comments: 'Bulk approved' })
      });
      alert(`Successfully approved ${result.succeeded} of ${result.total} items`);
      
      // Refresh
      await Promise.all([fetchStats(), fetchQueue()]);
      setSelectedIds([]);
      
    } catch (err) {
      console.error('Bulk approve error:', err);
      alert('Bulk approve failed: ' + err.message);
    }
  };
  
  // Handle selection
  const handleSelectItem = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id) 
        : [...prev, id]
    );
  };
  
  const handleSelectAll = (allIds) => {
    if (selectedIds.length === allIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };
  
  // Handle view evidence
  const handleViewEvidence = (item) => {
    navigate(`/evidence/${item.id}`);
  };
  
  // Tab config
  const tabs = [
    { id: 'my-approvals', label: 'My Approvals', icon: Clock, badge: stats?.pending },
    { id: 'team', label: 'Team Activity', icon: Users },
    { id: 'all-evidence', label: 'All Evidence', icon: Shield },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }
  ];
  
  return (
    <div className="space-y-0" data-testid="ownership-approval-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-[#6B6B6B] mb-1">
              <span>Evidence Hub</span>
              <span>›</span>
              <span className="text-white">Ownership & Approval</span>
            </div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              Ownership & Approval Center
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                <Shield size={10} className="inline mr-1" />
                Superpower #2
              </span>
            </h1>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Track accountability and manage approval workflows
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 hover:bg-[#1F1F1F] rounded-lg transition-colors text-[#6B6B6B] hover:text-white disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
              <HelpCircle size={16} />
              Guide
            </button>
            <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F] rounded-lg transition-colors">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
      </div>
      
      {/* Hero Stats */}
      <ApprovalHero stats={stats} userRole={stats?.userRole || currentUser?.role} />
      
      {/* Main Content */}
      <div className="pt-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-[#1F1F1F]">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px
                  ${isActive 
                    ? 'text-emerald-400 border-emerald-400' 
                    : 'text-[#6B6B6B] border-transparent hover:text-white hover:border-[#3F3F3F]'
                  }
                `}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        {activeTab === 'my-approvals' && (
          <MyApprovalsTab
            queue={queue}
            onApprove={handleApproveClick}
            onReject={handleRejectClick}
            onViewEvidence={handleViewEvidence}
            onBulkApprove={handleBulkApprove}
            selectedIds={selectedIds}
            onSelectItem={handleSelectItem}
            onSelectAll={handleSelectAll}
            isLoading={isLoading}
          />
        )}
        
        {activeTab === 'team' && (
          <TeamApprovalsTab
            teamActivity={teamActivity}
            isLoading={!teamActivity}
          />
        )}
        
        {activeTab === 'all-evidence' && (
          <AllEvidenceTab
            evidence={allEvidence}
            isLoading={!allEvidence}
            onPageChange={setCurrentPage}
            currentPage={currentPage}
            totalPages={Math.ceil((allEvidence?.total || 0) / ITEMS_PER_PAGE)}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        )}
        
        {activeTab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics}
            isLoading={!analytics}
          />
        )}
      </div>
      
      {/* Approval/Reject Modal */}
      <ApprovalModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, mode: 'approve', item: null, isSubmitting: false })}
        item={modalState.item}
        mode={modalState.mode}
        onSubmit={handleModalSubmit}
        isLoading={modalState.isSubmitting}
      />
    </div>
  );
}
